import { Directive, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { DataViewTypes, FeatureFlag } from '@mode/shared/contract-common';
import { ConfirmDialogService } from '@mode/shared/ui';
import { getInitialFlagValue, getThoughtspotIntegrated } from '@mode/shared/util-js';
import { ToastService } from '@mode/shared/util-toast';
import { DataViewFacade } from '@mode/switch-views/contract';
import { Subject, takeUntil } from 'rxjs';

type ReportModel = any;

@Directive({
  selector: '[modeDeleteReport]',
})
export class DeleteReportDirective implements OnDestroy {
  @Input() report: ReportModel | undefined;
  @Output() reportDeleted: EventEmitter<string> = new EventEmitter();

  enableNotebookPublishExportedDatasets = getInitialFlagValue(FeatureFlag.NotebookPublishExportedDatasets);
  enhancedDatasetResultsList: DataViewTypes.EnhancedDatasetResult[] = [];

  thoughtspotIntegrated = getThoughtspotIntegrated(document);

  private destroyed$ = new Subject<void>();
  private readonly title = 'Delete report?';
  private readonly successText = 'Removed report';
  private readonly buttonColor = 'alert';
  private readonly confirmText = 'Delete';
  private readonly deleteReportWarning = this.thoughtspotIntegrated
    ? '<b>Warning:</b> Deleting this report will delete all associated queries, charts.'
    : '<b>Warning:</b> Deleting this report will delete all associated queries, charts, and Explorations.';
  private readonly deletePublishedDatasetWarning = `
    <p>Deleting <b>Report</b> is irreversible and will permanently break content in the data workspace.</p>
    <p class='bold-text unset-margin'>The following will be deleted:</p>
    <ul>
      <li>all associated queries and runs</li>
      <li>any imported Datasets</li>
      <li>any local datasets created from Python output cells</li>
      <li>any calculated fields</li>
      <li>any associated charts</li>
      <li>any dependent Explorations</li>
    </ul>
    <p class='bold-text unset-margin'>The following will break:</p>
    <ul class='unset-margin'>
      <li>the published table(s) imported in the data workspace</li>
      <li>any dependent Models or Worksheets</li>
      <li>any dependent Reports or Liveboards</li>
      <li>any dependent Explorations</li>
      <li>any dependent Answers</li>
    </ul>
  `;

  constructor(
    private confirmDialogService: ConfirmDialogService,
    private toastService: ToastService,
    private dataViewFacade: DataViewFacade
  ) {
    this.dataViewFacade.enhancedDatasetResults$.pipe(takeUntil(this.destroyed$)).subscribe((enhancedDatasetResults) => {
      this.enhancedDatasetResultsList = [...enhancedDatasetResults];
    });
  }

  @HostListener('click', ['$event'])
  handleClick(event: MouseEvent): void {
    if (!this.report || !this.report.last_run_at) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const isPublishedDataset =
      this.enhancedDatasetResultsList.some((dataset) => dataset.thoughtspot) &&
      this.enableNotebookPublishExportedDatasets;
    const deleteDialogText = isPublishedDataset ? this.deletePublishedDatasetWarning : this.deleteReportWarning;
    const acknowledgeText = isPublishedDataset ? `I understand this will break content in the data workspace` : '';

    this.confirmDialogService.open({
      dialogTitle: this.title,
      dialogText: deleteDialogText,
      confirmText: this.confirmText,
      confirmButtonColor: this.buttonColor,
      acknowledgeText: acknowledgeText,
      confirmButtonType: 'primary',
      confirmAction: () => {
        this.report.destroy().then(() => {
          this.toastService.success(this.successText);
          this.reportDeleted.emit(this.report.token);
        });
      },
    });
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
